import Anthropic from '@anthropic-ai/sdk';
import type { Tool } from '@anthropic-ai/sdk/resources/messages';
import { GitLabDirectAccessClient } from './gitlab-direct-access';
import { GitLabError } from './gitlab-error';

import type {
  LanguageModelV2,
  LanguageModelV2CallOptions,
  LanguageModelV2StreamPart,
  LanguageModelV2FinishReason,
  LanguageModelV2CallWarning,
  LanguageModelV2Content,
  LanguageModelV2Usage,
  LanguageModelV2FunctionTool,
  LanguageModelV2Message,
  LanguageModelV2ToolChoice,
} from '@ai-sdk/provider';

export interface GitLabAnthropicConfig {
  provider: string;
  instanceUrl: string;
  getHeaders: () => Record<string, string>;
  fetch?: typeof fetch;

  /**
   * Optional callback to refresh the API key when a 401 error occurs.
   * Should clear cached credentials and re-fetch from auth provider.
   */
  refreshApiKey?: () => Promise<void>;

  /**
   * The Anthropic model to use (e.g., 'claude-sonnet-4-5-20250929')
   * @default 'claude-sonnet-4-5-20250929'
   */
  anthropicModel?: string;

  /**
   * Maximum tokens to generate
   * @default 8192
   */
  maxTokens?: number;

  /**
   * Feature flags to pass to the GitLab API
   * @default { DuoAgentPlatformNext: true }
   */
  featureFlags?: {
    DuoAgentPlatformNext: true;
  } & Record<string, boolean>;

  /**
   * AI Gateway URL for the Anthropic proxy.
   * Can also be set via GITLAB_AI_GATEWAY_URL environment variable.
   * @default 'https://cloud.gitlab.com'
   */
  aiGatewayUrl?: string;

  /**
   * Custom headers for AI Gateway Anthropic proxy requests.
   * Merged with headers from direct_access token response.
   */
  aiGatewayHeaders?: Record<string, string>;
}

/**
 * GitLab Anthropic Language Model
 *
 * This model uses GitLab's Anthropic proxy to provide native tool calling support
 * for the duo-chat model. It connects to Claude through GitLab's cloud proxy
 * at https://cloud.gitlab.com/ai/v1/proxy/anthropic/
 */
export class GitLabAnthropicLanguageModel implements LanguageModelV2 {
  readonly specificationVersion = 'v2' as const;
  readonly modelId: string;
  readonly supportedUrls: Record<string, RegExp[]> = {};

  private readonly config: GitLabAnthropicConfig;
  private readonly directAccessClient: GitLabDirectAccessClient;
  private anthropicClient: Anthropic | null = null;

  constructor(modelId: string, config: GitLabAnthropicConfig) {
    this.modelId = modelId;
    this.config = config;

    this.directAccessClient = new GitLabDirectAccessClient({
      instanceUrl: config.instanceUrl,
      getHeaders: config.getHeaders,
      refreshApiKey: config.refreshApiKey,
      fetch: config.fetch,
      featureFlags: config.featureFlags,
      aiGatewayUrl: config.aiGatewayUrl,
    });
  }

  get provider(): string {
    return this.config.provider;
  }

  /**
   * Get or create an Anthropic client with valid credentials
   * @param forceRefresh - If true, forces a token refresh before creating the client
   */
  private async getAnthropicClient(forceRefresh: boolean = false): Promise<Anthropic> {
    // Get token from GitLab (will use cache unless forceRefresh is true)
    const tokenData = await this.directAccessClient.getDirectAccessToken(forceRefresh);

    // Create new client with the token
    // GitLab's proxy expects:
    // - authToken: sets Authorization: Bearer header (NOT apiKey which sets x-api-key)
    // - defaultHeaders: additional headers from the direct access response
    // Filter out x-api-key from headers if present - we use authToken for Bearer auth
    const { 'x-api-key': _removed, ...filteredHeaders } = tokenData.headers;

    // Merge: tokenData headers < custom aiGatewayHeaders
    const mergedHeaders = {
      ...filteredHeaders,
      ...this.config.aiGatewayHeaders,
    };

    this.anthropicClient = new Anthropic({
      apiKey: null,
      authToken: tokenData.token,
      baseURL: this.directAccessClient.getAnthropicProxyUrl(),
      defaultHeaders: mergedHeaders,
    });

    return this.anthropicClient;
  }

  /**
   * Check if an error is a token-related authentication error that can be retried
   */
  private isTokenError(error: unknown): boolean {
    if (error instanceof Anthropic.APIError) {
      // 401 Unauthorized - token expired or revoked
      if (error.status === 401) {
        return true;
      }
      // Check for token-related error messages
      const message = error.message?.toLowerCase() || '';
      if (
        message.includes('token') &&
        (message.includes('expired') || message.includes('revoked') || message.includes('invalid'))
      ) {
        return true;
      }
    }
    return false;
  }

  /**
   * Check if an error is a context overflow error (prompt too long)
   * These should NOT trigger token refresh and should be reported to the user.
   */
  private isContextOverflowError(error: unknown): boolean {
    if (error instanceof Anthropic.APIError) {
      // 400 Bad Request with prompt too long message
      if (error.status === 400) {
        const message = error.message?.toLowerCase() || '';
        if (
          message.includes('prompt is too long') ||
          message.includes('prompt too long') ||
          (message.includes('tokens') && message.includes('maximum'))
        ) {
          return true;
        }
      }
    }
    return false;
  }

  /**
   * Convert AI SDK tools to Anthropic tool format
   */
  private convertTools(tools?: LanguageModelV2CallOptions['tools']): Tool[] | undefined {
    if (!tools || tools.length === 0) {
      return undefined;
    }

    return tools
      .filter((tool): tool is LanguageModelV2FunctionTool => tool.type === 'function')
      .map((tool) => {
        const schema = tool.inputSchema as { properties?: object; required?: string[] } | undefined;
        return {
          name: tool.name,
          description: tool.description || '',
          input_schema: {
            type: 'object' as const,
            properties: schema?.properties || {},
            required: schema?.required || [],
          },
        };
      });
  }

  /**
   * Convert AI SDK tool choice to Anthropic format
   */
  private convertToolChoice(
    toolChoice?: LanguageModelV2ToolChoice
  ): Anthropic.MessageCreateParams['tool_choice'] {
    if (!toolChoice) {
      return undefined;
    }

    switch (toolChoice.type) {
      case 'auto':
        return { type: 'auto' };
      case 'none':
        // Anthropic doesn't have a direct 'none' - we handle this by not sending tools
        return undefined;
      case 'required':
        return { type: 'any' };
      case 'tool':
        return { type: 'tool', name: toolChoice.toolName };
      default:
        return undefined;
    }
  }

  /**
   * Convert AI SDK prompt to Anthropic messages format
   */
  private convertPrompt(prompt: LanguageModelV2Message[]): {
    system?: string;
    messages: Anthropic.MessageParam[];
  } {
    let systemMessage: string | undefined;
    const messages: Anthropic.MessageParam[] = [];

    for (const message of prompt) {
      if (message.role === 'system') {
        systemMessage = systemMessage ? `${systemMessage}\n${message.content}` : message.content;
        continue;
      }

      if (message.role === 'user') {
        const content: Anthropic.ContentBlockParam[] = [];

        for (const part of message.content) {
          if (part.type === 'text') {
            content.push({ type: 'text', text: part.text });
          } else if (part.type === 'file') {
            // Handle file/image content if needed
            // For now, skip non-text content
          }
        }

        if (content.length > 0) {
          messages.push({ role: 'user', content });
        }
      } else if (message.role === 'assistant') {
        const content: Anthropic.ContentBlockParam[] = [];

        for (const part of message.content) {
          if (part.type === 'text') {
            content.push({ type: 'text', text: part.text });
          } else if (part.type === 'tool-call') {
            content.push({
              type: 'tool_use',
              id: part.toolCallId,
              name: part.toolName,
              input: typeof part.input === 'string' ? JSON.parse(part.input) : part.input,
            });
          }
        }

        if (content.length > 0) {
          messages.push({ role: 'assistant', content });
        }
      } else if (message.role === 'tool') {
        // Tool results need to be sent as user messages with tool_result content
        const content: Anthropic.ToolResultBlockParam[] = [];

        for (const part of message.content) {
          if (part.type === 'tool-result') {
            let resultContent: string;

            // Convert tool result output to string
            if (part.output.type === 'text') {
              resultContent = part.output.value;
            } else if (part.output.type === 'json') {
              resultContent = JSON.stringify(part.output.value);
            } else if (part.output.type === 'error-text') {
              resultContent = part.output.value;
            } else if (part.output.type === 'error-json') {
              resultContent = JSON.stringify(part.output.value);
            } else {
              resultContent = JSON.stringify(part.output);
            }

            content.push({
              type: 'tool_result',
              tool_use_id: part.toolCallId,
              content: resultContent,
              is_error: part.output.type.startsWith('error'),
            });
          }
        }

        if (content.length > 0) {
          messages.push({ role: 'user', content });
        }
      }
    }

    return { system: systemMessage, messages };
  }

  /**
   * Convert Anthropic finish reason to AI SDK format
   */
  private convertFinishReason(stopReason: string | null): LanguageModelV2FinishReason {
    switch (stopReason) {
      case 'end_turn':
        return 'stop';
      case 'stop_sequence':
        return 'stop';
      case 'max_tokens':
        return 'length';
      case 'tool_use':
        return 'tool-calls';
      default:
        return 'unknown';
    }
  }

  async doGenerate(options: LanguageModelV2CallOptions): Promise<{
    content: LanguageModelV2Content[];
    finishReason: LanguageModelV2FinishReason;
    usage: LanguageModelV2Usage;
    warnings: LanguageModelV2CallWarning[];
  }> {
    return this.doGenerateWithRetry(options, false);
  }

  private async doGenerateWithRetry(
    options: LanguageModelV2CallOptions,
    isRetry: boolean
  ): Promise<{
    content: LanguageModelV2Content[];
    finishReason: LanguageModelV2FinishReason;
    usage: LanguageModelV2Usage;
    warnings: LanguageModelV2CallWarning[];
  }> {
    const client = await this.getAnthropicClient(isRetry);
    const { system, messages } = this.convertPrompt(options.prompt);
    const tools = this.convertTools(options.tools);
    const toolChoice =
      options.toolChoice?.type !== 'none' ? this.convertToolChoice(options.toolChoice) : undefined;

    const anthropicModel = this.config.anthropicModel || 'claude-sonnet-4-5-20250929';
    const maxTokens = options.maxOutputTokens || this.config.maxTokens || 8192;

    try {
      const response = await client.messages.create({
        model: anthropicModel,
        max_tokens: maxTokens,
        system: system,
        messages: messages,
        tools: tools,
        tool_choice: tools ? toolChoice : undefined,
        temperature: options.temperature,
        top_p: options.topP,
        stop_sequences: options.stopSequences,
      });

      const content: LanguageModelV2Content[] = [];

      for (const block of response.content) {
        if (block.type === 'text') {
          content.push({
            type: 'text',
            text: block.text,
          });
        } else if (block.type === 'tool_use') {
          content.push({
            type: 'tool-call',
            toolCallId: block.id,
            toolName: block.name,
            input: JSON.stringify(block.input),
          });
        }
      }

      const usage: LanguageModelV2Usage = {
        inputTokens: response.usage.input_tokens,
        outputTokens: response.usage.output_tokens,
        totalTokens: response.usage.input_tokens + response.usage.output_tokens,
      };

      return {
        content,
        finishReason: this.convertFinishReason(response.stop_reason),
        usage,
        warnings: [],
      };
    } catch (error) {
      // Check for context overflow FIRST - these should NOT trigger token refresh
      if (this.isContextOverflowError(error)) {
        const apiError = error as InstanceType<typeof Anthropic.APIError>;
        throw new GitLabError({
          message: `Context overflow: ${apiError.message}. Please start a new session or use /compact to reduce context.`,
          statusCode: 400,
          cause: error,
        });
      }

      // If this is a token error and we haven't retried yet, refresh token and retry
      if (!isRetry && this.isTokenError(error)) {
        this.directAccessClient.invalidateToken();
        return this.doGenerateWithRetry(options, true);
      }

      if (error instanceof Anthropic.APIError) {
        throw new GitLabError({
          message: `Anthropic API error: ${error.message}`,
          statusCode: error.status,
          cause: error,
        });
      }
      throw error;
    }
  }

  async doStream(options: LanguageModelV2CallOptions): Promise<{
    stream: ReadableStream<LanguageModelV2StreamPart>;
    request?: { body?: unknown };
    response?: { headers?: Record<string, string> };
  }> {
    return this.doStreamWithRetry(options, false);
  }

  private async doStreamWithRetry(
    options: LanguageModelV2CallOptions,
    isRetry: boolean
  ): Promise<{
    stream: ReadableStream<LanguageModelV2StreamPart>;
    request?: { body?: unknown };
    response?: { headers?: Record<string, string> };
  }> {
    const client = await this.getAnthropicClient(isRetry);
    const { system, messages } = this.convertPrompt(options.prompt);
    const tools = this.convertTools(options.tools);
    const toolChoice =
      options.toolChoice?.type !== 'none' ? this.convertToolChoice(options.toolChoice) : undefined;

    const anthropicModel = this.config.anthropicModel || 'claude-sonnet-4-5-20250929';
    const maxTokens = options.maxOutputTokens || this.config.maxTokens || 8192;

    const requestBody = {
      model: anthropicModel,
      max_tokens: maxTokens,
      system: system,
      messages: messages,
      tools: tools,
      tool_choice: tools ? toolChoice : undefined,
      temperature: options.temperature,
      top_p: options.topP,
      stop_sequences: options.stopSequences,
      stream: true as const,
    };

    // Reference to this for use in the stream callback
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const self = this;

    const stream = new ReadableStream<LanguageModelV2StreamPart>({
      start: async (controller) => {
        const contentBlocks: Record<
          number,
          | { type: 'text'; id: string }
          | { type: 'tool-call'; toolCallId: string; toolName: string; input: string }
        > = {};

        const usage: LanguageModelV2Usage = {
          inputTokens: 0,
          outputTokens: 0,
          totalTokens: 0,
        };
        let finishReason: LanguageModelV2FinishReason = 'unknown';

        try {
          const anthropicStream = client.messages.stream(requestBody, {
            signal: options.abortSignal,
          });

          // Stream start
          controller.enqueue({
            type: 'stream-start',
            warnings: [],
          });

          // Use event-based approach instead of for-await to ensure all events are received
          await new Promise<void>((resolve, reject) => {
            anthropicStream.on('streamEvent', (event) => {
              try {
                switch (event.type) {
                  case 'message_start':
                    if (event.message.usage) {
                      usage.inputTokens = event.message.usage.input_tokens;
                    }
                    controller.enqueue({
                      type: 'response-metadata',
                      id: event.message.id,
                      modelId: event.message.model,
                    });
                    break;

                  case 'content_block_start':
                    if (event.content_block.type === 'text') {
                      const textId = `text-${event.index}`;
                      contentBlocks[event.index] = { type: 'text', id: textId };
                      controller.enqueue({
                        type: 'text-start',
                        id: textId,
                      });
                    } else if (event.content_block.type === 'tool_use') {
                      contentBlocks[event.index] = {
                        type: 'tool-call',
                        toolCallId: event.content_block.id,
                        toolName: event.content_block.name,
                        input: '',
                      };
                      controller.enqueue({
                        type: 'tool-input-start',
                        id: event.content_block.id,
                        toolName: event.content_block.name,
                      });
                    }
                    break;

                  case 'content_block_delta': {
                    const block = contentBlocks[event.index];
                    if (event.delta.type === 'text_delta' && block?.type === 'text') {
                      controller.enqueue({
                        type: 'text-delta',
                        id: block.id,
                        delta: event.delta.text,
                      });
                    } else if (
                      event.delta.type === 'input_json_delta' &&
                      block?.type === 'tool-call'
                    ) {
                      block.input += event.delta.partial_json;
                      controller.enqueue({
                        type: 'tool-input-delta',
                        id: block.toolCallId,
                        delta: event.delta.partial_json,
                      });
                    }
                    break;
                  }

                  case 'content_block_stop': {
                    const block = contentBlocks[event.index];
                    if (block?.type === 'text') {
                      controller.enqueue({
                        type: 'text-end',
                        id: block.id,
                      });
                    } else if (block?.type === 'tool-call') {
                      controller.enqueue({
                        type: 'tool-input-end',
                        id: block.toolCallId,
                      });

                      controller.enqueue({
                        type: 'tool-call',
                        toolCallId: block.toolCallId,
                        toolName: block.toolName,
                        input: block.input === '' ? '{}' : block.input,
                      });
                    }
                    delete contentBlocks[event.index];
                    break;
                  }

                  case 'message_delta':
                    if (event.usage) {
                      usage.outputTokens = event.usage.output_tokens;
                      usage.totalTokens = (usage.inputTokens || 0) + event.usage.output_tokens;
                    }
                    if (event.delta.stop_reason) {
                      finishReason = self.convertFinishReason(event.delta.stop_reason);
                    }
                    break;

                  case 'message_stop': {
                    controller.enqueue({
                      type: 'finish',
                      finishReason,
                      usage,
                    });
                    break;
                  }
                }
              } catch (error) {
                controller.enqueue({
                  type: 'error',
                  error: error instanceof Error ? error : new Error(String(error)),
                });
              }
            });

            anthropicStream.on('end', () => {
              resolve();
            });

            anthropicStream.on('error', (error) => {
              reject(error);
            });
          });

          // Emit any pending tool calls if stream ended without content_block_stop
          for (const [, block] of Object.entries(contentBlocks)) {
            if (block.type === 'tool-call') {
              controller.enqueue({
                type: 'tool-input-end',
                id: block.toolCallId,
              });
              controller.enqueue({
                type: 'tool-call',
                toolCallId: block.toolCallId,
                toolName: block.toolName,
                input: block.input === '' ? '{}' : block.input,
              });
            }
          }

          controller.close();
        } catch (error) {
          // Emit any pending tool calls before handling the error
          for (const [, block] of Object.entries(contentBlocks)) {
            if (block.type === 'tool-call') {
              controller.enqueue({
                type: 'tool-input-end',
                id: block.toolCallId,
              });
              controller.enqueue({
                type: 'tool-call',
                toolCallId: block.toolCallId,
                toolName: block.toolName,
                input: block.input === '' ? '{}' : block.input,
              });
            }
          }

          // Check for context overflow FIRST - these should NOT trigger token refresh
          if (self.isContextOverflowError(error)) {
            const apiError = error as InstanceType<typeof Anthropic.APIError>;
            controller.enqueue({
              type: 'error',
              error: new GitLabError({
                message: `Context overflow: ${apiError.message}. Please start a new session or use /compact to reduce context.`,
                statusCode: 400,
                cause: error,
              }),
            });
            controller.close();
            return;
          }

          // If this is a token error and we haven't retried yet, we need to signal retry
          // For streaming, we close this stream with an error and the caller should retry
          if (!isRetry && self.isTokenError(error)) {
            self.directAccessClient.invalidateToken();
            // Signal that a retry is needed with a special error
            controller.enqueue({
              type: 'error',
              error: new GitLabError({
                message: 'TOKEN_REFRESH_NEEDED',
                cause: error,
              }),
            });
            controller.close();
            return;
          }

          if (error instanceof Anthropic.APIError) {
            controller.enqueue({
              type: 'error',
              error: new GitLabError({
                message: `Anthropic API error: ${error.message}`,
                statusCode: error.status,
                cause: error,
              }),
            });
          } else {
            controller.enqueue({
              type: 'error',
              error,
            });
          }
          controller.close();
        }
      },
    });

    return {
      stream,
      request: { body: requestBody },
    };
  }
}
